import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from 'ngx-toastr';
import {
  combineLatest,
  filter,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  GradeLesson,
  GradeLessonStatus,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { UserModel } from '../../../../models/user.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { AuthService } from '../../../../services/auth.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as lessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as lessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import {
  AlertType,
  DlgConfirmComponent,
} from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { DlgUpsertGradeLessonComponent } from '../dlg-upsert-grade-lesson/dlg-upsert-grade-lesson.component';

@Component({
  selector: 'app-list-grade-lessons',
  standalone: true,
  imports: [
    AsyncPipe,
    RouterLink,
    MatDialogModule,
    BtnChipComponent,
    BtnFabComponent,
    BtnMoreComponent,
    ListUtilsBarComponent,
    NgxSkeletonLoaderModule,
    MomentPipe,
  ],
  templateUrl: './list-grade-lessons.component.html',
  styleUrl: './list-grade-lessons.component.css',
})
export class ListGradeLessonsComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private store: Store<AppState> = inject(Store<AppState>);
  logger: NGXLogger = inject(NGXLogger);
  toastr: ToastrService = inject(ToastrService);
  utilsService: UtilsService = inject(UtilsService);
  private dialog: MatDialog = inject(MatDialog);
  authService: AuthService = inject(AuthService);

  groupId: string | null = this.route.root.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.root.snapshot.paramMap.get('facultyId');
  gradeId: string | null = this.route.root.snapshot.paramMap.get('gradeId');
  academicYear: number | undefined = undefined;

  user: UserModel | null = null;
  teacher: SchoolMember | null | undefined = null;
  group: Group | null | undefined;
  grade: Grade | null | undefined = null;

  lessons$: Observable<GradeLesson[] | null | undefined> = new Observable();

  loading$: Observable<boolean> = new Observable();
  inProgress$: Observable<boolean> = new Observable();

  membershipStatus = MembershipStatus;
  lessonStatus = GradeLessonStatus;
  rolesType = SchoolRoles;
  utils = UtilsService;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject<void>();

  private params$ = combineLatest({
    // Use .parent if the component is in a child router-outlet
    params: this.route.paramMap,
    // Use queryParams from the current route
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        facultyId: pMap.get('facultyId'),
        gradeId: pMap.get('gradeId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.loading$ = this.store
      .select(lessonSelector.loading)
      .pipe(takeUntil(this.exit$));

    this.inProgress$ = this.store
      .pipe(select(lessonSelector.inProgress))
      .pipe(takeUntil(this.exit$));

    this.myRoles$ = this.store
      .select(groupSelector.getMyRolesInGroup(this.groupId))
      .pipe(takeUntil(this.exit$));
  }

  ngOnInit(): void {
    this.params$.subscribe(({ groupId, facultyId, gradeId, academicYear }) => {
      this.groupId = groupId;
      this.facultyId = facultyId;
      this.gradeId = gradeId;
      this.academicYear = academicYear;

      this.logger.debug(
        `ListSessionsComponent :: Received route params groupId=${groupId}, facultyId=${facultyId}, gradeId=${gradeId}, academicYear=${academicYear}`,
      );

      if (groupId) {
        this.loadData(groupId, facultyId, gradeId, academicYear);
      }
    });
  }

  private loadData(
    groupId: string,
    facultyId: string | null,
    gradeId: string | null,
    academicYear?: number,
  ) {
    this.store
      .select(groupSelector.getGroupById(this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        const effectiveYear = academicYear ?? group?.academicYear;
        this.academicYear = effectiveYear;

        this.logger.debug(
          `ListGradeLessonsComponent :: Loaded group ${group?.name} with academicYear=${group?.academicYear}. Effective academic year for sessions: ${effectiveYear}`,
        );

        if (effectiveYear) {
          this.initDataStreams(groupId, facultyId, gradeId, effectiveYear);
        }
      });
  }

  private initDataStreams(
    groupId: string,
    facultyId: string | null,
    gradeId: string | null,
    year: number,
  ) {
    this.logger.debug(
      `ListGradeLessonsComponent :: Initializing data streams with groupId=${groupId}, facultyId=${facultyId}, gradeId=${gradeId}, year=${year}`,
    );

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    if (gradeId) {
      this.setupGradeAndLessons(groupId, gradeId, year);
    } else if (facultyId) {
      this.store
        .select(memberSelector.getSchoolMemberById(facultyId, groupId))
        .pipe(
          filter((m) => !!m),
          takeUntil(this.exit$),
        )
        .subscribe((teacher) => {
          this.teacher = teacher;
          this.logger.debug(
            `ListGradeLessonsComponent :: Loaded teacher ${teacher?.name} with gradeId=${teacher?.gradeId}`,
          );
          if (teacher?.gradeId) {
            this.setupGradeAndLessons(groupId, teacher.gradeId, year);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  setupGradeAndLessons(groupId: string, gradeId: string, academicYear: number) {
    this.logger.debug(
      `ListGradeLessonsComponent :: loadContent :: gradeId: ${this.gradeId}, facultyId: ${this.facultyId}, groupId: ${this.groupId}, academicYear: ${this.academicYear}`,
    );
    this.lessons$ = this.store.select(
      lessonSelector.getGradeLessonsByGradeId({
        gradeId,
        groupId: this.groupId!,
        academicYear,
        lessonStatus: [GradeLessonStatus.open, GradeLessonStatus.closed],
      }),
    );

    // 2. Load the Grade object for UI labels/titles
    this.store
      .select(
        gradeSelector.getGradeById({
          gradeId,
          groupId: this.groupId!,
          academicYear,
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((grade) => {
        this.grade = grade;
        this.logger.debug(
          `ListGradeLessonsComponent :: Loaded grade ${grade?.gradeName} with teacherId=${grade?.teacherId}`,
        );

        // If the grade has a teacherId we didn't have before, load that faculty too
        if (grade?.teacherId) {
          this.store
            .select(
              memberSelector.getSchoolMemberById(grade.teacherId, groupId),
            )
            .pipe(take(1))
            .subscribe((t) => (this.teacher = t));
        }
      });

    // 3. Trigger the initial Dispatch if the store is empty
    this.lessons$.pipe(take(1)).subscribe((lessons) => {
      if (!lessons || lessons.length === 0) {
        this.logger.debug(
          `ListGradeLessonsComponent :: No lessons found for gradeId=${gradeId}, year=${academicYear}. Dispatching getLessons action.`,
        );
        this.store.dispatch(
          lessonActions.getGradeLessons({
            groupId,
            gradeId,
            academicYear: academicYear,
            status: [GradeLessonStatus.open, GradeLessonStatus.closed],
          }),
        );
      }
    });
  }

  addMore() {
    if (this.groupId === null || this.grade?.id === null) return;
    this.lessons$.pipe(take(1)).subscribe((lessons) => {
      this.store.dispatch(
        lessonActions.getGradeLessons({
          groupId: this.groupId!,
          gradeId: this.grade?.id!,
          academicYear: this.group?.academicYear!,
          status: [GradeLessonStatus.open, GradeLessonStatus.closed],
          lastRecord:
            (lessons ?? [])?.length > 0
              ? lessons![lessons!.length - 1]?.modified
              : undefined,
        }),
      );
    });
  }

  onRefresh() {
    if (this.groupId === null || this.grade?.id === null) return;
    this.store.dispatch(
      lessonActions.getGradeLessons({
        groupId: this.groupId!,
        gradeId: this.grade?.id!,
        academicYear: this.group?.academicYear!,
        status: [GradeLessonStatus.open, GradeLessonStatus.closed],
      }),
    );
  }

  onEdit(item: GradeLesson) {
    // console.log(`DetailFacultyComponent :: onEditLesson called`);
    if (!this.groupId || this.grade?.id === undefined) return;

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Update Grade Lesson',
      description: 'Update grade lesson details',
      action: 'Update',
      lesson: item,
    };

    const dialogRef = this.dialog.open(
      DlgUpsertGradeLessonComponent,
      dialogConfig,
    );
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success(
          'The grade lesson details have been updated successfully.',
          'Grade Lesson Updated',
        );
      }
      return;
    });
  }

  onDelete(item: GradeLesson) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Delete Lesson',
      description: `Are you sure you want to delete lesson '${item.lessonName}'?`,
      action: 'Delete',
      buttonColor: 'bg-red-500',
      showType: AlertType.error,
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result && item.groupId) {
        return this.store.dispatch(
          lessonActions.deleteAction({ groupId: item.groupId, id: item.id! }),
        );
      }
      return;
    });
  }

  onToggleLessonStatus(item: GradeLesson) {
    return this.store.dispatch(
      lessonActions.update({
        groupId: item.groupId!,
        lessonId: item.id!,
        gradeLesson: {
          lessonStatus:
            item.lessonStatus == GradeLessonStatus.closed
              ? GradeLessonStatus.open
              : GradeLessonStatus.closed,
        },
      }),
    );
  }

  onToggleLessonArchival(lesson: GradeLesson, action: GradeLessonStatus) {
    if (
      action == GradeLessonStatus.archived &&
      lesson.lessonStatus !== GradeLessonStatus.closed
    ) {
      this.utilsService.onShowAlert(
        {
          title: 'Lesson Not Closed',
          message: `You can only archive lessons that are closed. Please close the lesson '${lesson.lessonName}' before archiving.`,
        } as AppExceptionModel,
        false,
      );
      return;
    } else if (
      action == GradeLessonStatus.open &&
      lesson.lessonStatus !== GradeLessonStatus.archived
    ) {
      this.utilsService.onShowAlert({
        title: 'Lesson Not Archived',
        message: `You can only unarchive lessons that are archived. Please archive the lesson '${lesson.lessonName}' before unarchiving.`,
      } as AppExceptionModel);
      return;
    }
    if (!lesson.groupId || !lesson.id) return;

    let dialogConfig = new MatDialogConfig();
    dialogConfig.data =
      action == GradeLessonStatus.archived
        ? {
            title: 'Archive Lesson',
            description: `Are you sure you want to archive the lesson '${lesson.lessonName}' for '${this.grade?.gradeName}' grade?`,
            action: 'Archive',
            buttonColor: 'bg-red-500',
            showType: AlertType.warning,
          }
        : {
            title: 'Unarchive Lesson',
            description: `Are you sure you want to unarchive the lesson '${lesson.lessonName}' for '${this.grade?.gradeName}' grade?`,
            action: 'Unarchive',
            buttonColor: 'bg-green-500',
            showType: AlertType.info,
          };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        return this.store.dispatch(
          lessonActions.update({
            groupId: lesson.groupId!,
            lessonId: lesson.id!,
            gradeLesson: {
              ...lesson,
              lessonStatus: action,
            },
          }),
        );
      }
      return;
    });
  }

  isLessonViewable(publishBy?: Timestamp): boolean {
    if (!publishBy) return false;
    const currentTime = Timestamp.now();
    return publishBy.toMillis() <= currentTime.toMillis();
  }
}
